/**
 * Application Settings Component
 * 
 * Comprehensive settings management with MongoDB integration
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentUser } from '../services/userService';
import { getSettings, updateSettings, changePassword, removeSession, removeAllSessions, getApiKeys, updateApiKeys } from '../services/settingsService';

export default function ApplicationSettings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  
  const [settings, setSettings] = useState({
    two_factor_auth: {
      authenticator_enabled: false,
      email_2fa_enabled: false,
      sms_2fa_enabled: false,
      recovery_codes_generated: false
    },
    active_sessions: [],
    login_alerts: {
      email_alerts_new_login: true,
      failed_login_attempts: true,
      unusual_activity: true,
      password_change_confirmation: true,
      security_recommendations: false
    },
    data_privacy: {
      data_retention_days: 30,
      allow_data_export: true,
      analytics_enabled: true
    },
    advanced_security: {
      require_2fa_for_sensitive_actions: true,
      auto_logout_inactive_minutes: 0,
      ip_allowlist_enabled: false,
      allowed_ip_addresses: [],
      account_lockout_attempts: 5,
      account_lockout_duration_minutes: 30
    },
    security_score: 0,
    last_password_change: null
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    text: 'Not set',
    requirements: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false
    }
  });

  const [apiKeysMasked, setApiKeysMasked] = useState(null);
  const [apiKeysForm, setApiKeysForm] = useState({
    openai_api_key: '',
    anthropic_api_key: '',
    serpapi_api_key: '',
    inference_api_key: ''
  });
  const [apiKeysSaving, setApiKeysSaving] = useState(false);

  // Load user and settings on mount
  useEffect(() => {
    loadUserAndSettings();
  }, []);

  // Check password strength
  useEffect(() => {
    checkPasswordStrength(passwordForm.new_password);
  }, [passwordForm.new_password]);

  const loadUserAndSettings = async () => {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      setUserId(user.id);
      
      const userSettings = await getSettings(user.id);
      setSettings(userSettings);
      
    } catch (error) {
      console.error('Error loading user and settings:', error);
      alert('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadApiKeys = async () => {
    try {
      const data = await getApiKeys();
      setApiKeysMasked(data);
    } catch (e) {
      console.error('Error loading API keys:', e);
      setApiKeysMasked({});
    }
  };

  const handleSaveApiKeys = async () => {
    try {
      setApiKeysSaving(true);
      const payload = {};
      if (apiKeysForm.openai_api_key !== '') payload.openai_api_key = apiKeysForm.openai_api_key;
      if (apiKeysForm.anthropic_api_key !== '') payload.anthropic_api_key = apiKeysForm.anthropic_api_key;
      if (apiKeysForm.serpapi_api_key !== '') payload.serpapi_api_key = apiKeysForm.serpapi_api_key;
      if (apiKeysForm.inference_api_key !== '') payload.inference_api_key = apiKeysForm.inference_api_key;
      await updateApiKeys(payload);
      await loadApiKeys();
      setApiKeysForm({ openai_api_key: '', anthropic_api_key: '', serpapi_api_key: '', inference_api_key: '' });
      alert(t('settings.apiKeysSaved') || 'API keys saved.');
    } catch (e) {
      console.error('Error saving API keys:', e);
      alert(e.response?.data?.detail || 'Failed to save API keys.');
    } finally {
      setApiKeysSaving(false);
    }
  };

  const checkPasswordStrength = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*]/.test(password)
    };

    const metCount = Object.values(requirements).filter(Boolean).length;
    
    let score = 0;
    let text = 'Not set';
    
    if (metCount <= 2) {
      score = 1;
      text = 'Weak';
    } else if (metCount <= 4) {
      score = 2;
      text = 'Medium';
    } else {
      score = 3;
      text = 'Strong';
    }

    setPasswordStrength({ score, text, requirements });
  };

  const handleToggleChange = (section, field) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field]
      }
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await updateSettings(userId, {
        two_factor_auth: settings.two_factor_auth,
        login_alerts: settings.login_alerts,
        data_privacy: settings.data_privacy,
        advanced_security: settings.advanced_security
      });
      alert('✅ Settings saved successfully!');
      await loadUserAndSettings(); // Reload to get updated security score
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('❌ Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async () => {
    try {
      if (passwordForm.new_password !== passwordForm.confirm_password) {
        alert('❌ Passwords do not match!');
        return;
      }

      if (passwordStrength.score < 2) {
        alert('❌ Password is too weak! Please use a stronger password.');
        return;
      }

      await changePassword(userId, passwordForm);
      alert('✅ Password updated successfully!');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      await loadUserAndSettings();
    } catch (error) {
      console.error('Error updating password:', error);
      alert('❌ Failed to update password');
    }
  };

  const handleSignOutSession = async (sessionId) => {
    try {
      await removeSession(userId, sessionId);
      alert('✅ Session signed out successfully!');
      await loadUserAndSettings();
    } catch (error) {
      console.error('Error signing out session:', error);
      alert('❌ Failed to sign out session');
    }
  };

  const handleSignOutAllDevices = async () => {
    if (!confirm('Are you sure you want to sign out all other devices?')) return;
    
    try {
      await removeAllSessions(userId, true);
      alert('✅ All other devices signed out successfully!');
      await loadUserAndSettings();
    } catch (error) {
      console.error('Error signing out all devices:', error);
      alert('❌ Failed to sign out all devices');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 60) return '1 month ago';
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const getSecurityScoreColor = (score) => {
    if (score >= 80) return 'from-primary-dark to-primary-500';
    if (score >= 60) return 'from-amber-600 to-amber-400';
    return 'from-red-600 to-red-400';
  };

  const getSecurityScoreText = (score) => {
    if (score >= 80) return 'Strong Security';
    if (score >= 60) return 'Good Security';
    if (score >= 40) return 'Fair Security';
    return 'Weak Security';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-dark-200">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-light-200/20 shadow-sm">
        <div className="px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div>
              <h1 className="text-2xl font-bold text-dark-300 flex items-center">
                <i className="fas fa-sliders text-primary-500 mr-3"></i>
                {t('settings.title')}
              </h1>
              <p className="text-sm text-dark-100 mt-0.5">{t('settings.subtitle')}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <button 
              onClick={loadUserAndSettings}
              className="px-4 py-2 bg-light-100 hover:bg-light-200/20 rounded-lg text-sm font-medium text-dark-300 transition-colors flex items-center"
            >
              <i className="fas fa-rotate mr-2"></i>
              {t('schemaEditor.resetToDefault')}
            </button>
            
            <button 
              onClick={handleSaveSettings}
              disabled={saving}
              className="px-4 py-2 bg-gradient-to-r from-primary-dark to-primary-500 hover:opacity-90 text-white rounded-lg text-sm font-semibold transition-all shadow-md flex items-center disabled:opacity-50"
            >
              <i className="fas fa-floppy-disk mr-2"></i>
              {saving ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="px-8 flex space-x-6 border-t border-light-200/10">
          <button 
            onClick={() => setActiveTab('general')}
            className={`px-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'general' ? 'text-primary-500 border-primary-500' : 'text-dark-100 hover:text-dark-300 border-transparent hover:border-light-200/30'}`}
          >
            {t('settings.general')}
          </button>
          <button 
            onClick={() => setActiveTab('security')}
            className={`px-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'security' ? 'text-primary-500 border-primary-500' : 'text-dark-100 hover:text-dark-300 border-transparent hover:border-light-200/30'}`}
          >
            {t('settings.security')}
          </button>
          <button 
            onClick={() => setActiveTab('notifications')}
            className={`px-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'notifications' ? 'text-primary-500 border-primary-500' : 'text-dark-100 hover:text-dark-300 border-transparent hover:border-light-200/30'}`}
          >
            {t('settings.notifications')}
          </button>
          <button 
            onClick={() => setActiveTab('privacy')}
            className={`px-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'privacy' ? 'text-primary-500 border-primary-500' : 'text-dark-100 hover:text-dark-300 border-transparent hover:border-light-200/30'}`}
          >
            {t('settings.privacy')}
          </button>
          <button 
            onClick={() => setActiveTab('advanced')}
            className={`px-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'advanced' ? 'text-primary-500 border-primary-500' : 'text-dark-100 hover:text-dark-300 border-transparent hover:border-light-200/30'}`}
          >
            {t('settings.advanced')}
          </button>
          <button 
            onClick={() => { setActiveTab('apikeys'); if (apiKeysMasked === null) loadApiKeys(); }}
            className={`px-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'apikeys' ? 'text-primary-500 border-primary-500' : 'text-dark-100 hover:text-dark-300 border-transparent hover:border-light-200/30'}`}
          >
            {t('settings.apiKeys') || 'API Keys'}
          </button>
        </div>
      </header>

      {/* Page Header Section */}
      <section className="bg-white border-b border-light-200/20 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-dark-300 mb-2">{t('settings.generalSettings')}</h2>
            <p className="text-dark-100">{t('settings.generalSettingsDesc')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-light-200/20">
              <div className="text-xs text-dark-100 mb-0.5">{t('settings.lastUpdated')}</div>
              <div className="text-lg font-bold text-primary-500">{formatTimeAgo(settings.updated_at)}</div>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-light-200/20">
              <div className="text-xs text-dark-100 mb-0.5">{t('settings.systemStatus')}</div>
              <div className="text-lg font-bold text-primary-dark">{t('settings.healthy')}</div>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-light-200/20">
              <div className="text-xs text-dark-100 mb-0.5">{t('settings.backup')}</div>
              <div className="text-lg font-bold text-primary-500">{t('settings.enabled')}</div>
            </div>
          </div>
        </div>
      </section>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Settings Area */}
        <main className="flex-1 overflow-y-auto px-8 py-6">
          
          {/* Security Tab */}
          {activeTab === 'security' && (
            <>
              {/* Password Change Section */}
              <section className="mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                  <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                    <i className="fas fa-shield-halved text-primary-500 mr-2"></i>
                    {t('settings.accountSecurity')}
                  </h3>
                  
                  <div className="p-6 bg-primary-500/5 border-2 border-primary-500/20 rounded-xl">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-bold text-dark-300 mb-2 flex items-center">
                          <i className="fas fa-key text-primary-500 mr-2"></i>
                          {t('settings.changePassword')}
                        </h4>
                        <p className="text-sm text-dark-100">{t('settings.changePasswordDesc')}</p>
                      </div>
                      <div className="px-3 py-1 bg-primary-500/10 text-primary-dark text-xs font-bold rounded-full">
                        {t('settings.lastChanged')}: {formatTimeAgo(settings.last_password_change)}
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-2">
                          <span className="text-sm font-bold text-dark-300">{t('settings.currentPassword')} <span className="text-primary-500">*</span></span>
                        </label>
                        <input 
                          type="password" 
                          name="current_password"
                          value={passwordForm.current_password}
                          onChange={handlePasswordChange}
                          placeholder={t('settings.currentPasswordPlaceholder')}
                          className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                        />
                      </div>
                      
                      <div>
                        <label className="block mb-2">
                          <span className="text-sm font-bold text-dark-300">{t('settings.newPassword')} <span className="text-primary-500">*</span></span>
                        </label>
                        <input 
                          type="password" 
                          name="new_password"
                          value={passwordForm.new_password}
                          onChange={handlePasswordChange}
                          placeholder={t('settings.newPasswordPlaceholder')}
                          className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                        />
                        <div className="mt-2 bg-light-100 rounded-lg overflow-hidden h-1">
                          <div 
                            className={`h-full transition-all ${
                              passwordStrength.score === 1 ? 'w-1/3 bg-red-500' :
                              passwordStrength.score === 2 ? 'w-2/3 bg-amber-500' :
                              passwordStrength.score === 3 ? 'w-full bg-primary-500' : 'w-0'
                            }`}
                          />
                        </div>
                        <p className="text-xs text-dark-100 mt-2">{t('settings.passwordStrength')}: {passwordStrength.text}</p>
                      </div>
                      
                      <div>
                        <label className="block mb-2">
                          <span className="text-sm font-bold text-dark-300">{t('settings.confirmPassword')} <span className="text-primary-500">*</span></span>
                        </label>
                        <input 
                          type="password" 
                          name="confirm_password"
                          value={passwordForm.confirm_password}
                          onChange={handlePasswordChange}
                          placeholder={t('settings.confirmPasswordPlaceholder')}
                          className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                        />
                      </div>
                      
                      <div className="bg-white border-2 border-light-200/20 rounded-lg p-4">
                        <p className="text-sm font-semibold text-dark-300 mb-3">{t('settings.passwordRequirements')}:</p>
                        <ul className="space-y-2 text-sm text-dark-100">
                          <li className="flex items-center">
                            <i className={`fas fa-check-circle mr-2 ${passwordStrength.requirements.length ? 'text-primary-500' : 'text-light-200'}`}></i>
                            {t('settings.req8chars')}
                          </li>
                          <li className="flex items-center">
                            <i className={`fas fa-check-circle mr-2 ${passwordStrength.requirements.uppercase ? 'text-primary-500' : 'text-light-200'}`}></i>
                            {t('settings.reqUppercase')}
                          </li>
                          <li className="flex items-center">
                            <i className={`fas fa-check-circle mr-2 ${passwordStrength.requirements.lowercase ? 'text-primary-500' : 'text-light-200'}`}></i>
                            {t('settings.reqLowercase')}
                          </li>
                          <li className="flex items-center">
                            <i className={`fas fa-check-circle mr-2 ${passwordStrength.requirements.number ? 'text-primary-500' : 'text-light-200'}`}></i>
                            {t('settings.reqNumber')}
                          </li>
                          <li className="flex items-center">
                            <i className={`fas fa-check-circle mr-2 ${passwordStrength.requirements.special ? 'text-primary-500' : 'text-light-200'}`}></i>
                            {t('settings.reqSpecial')}
                          </li>
                        </ul>
                      </div>
                      
                      <div className="flex items-center justify-end pt-4 border-t border-light-200/20">
                        <button 
                          onClick={handlePasswordUpdate}
                          className="px-6 py-3 bg-gradient-to-r from-primary-dark to-primary-500 hover:opacity-90 text-white rounded-lg font-semibold shadow-md transition-all flex items-center"
                        >
                          <i className="fas fa-check mr-2"></i>
                          {t('settings.updatePassword')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 2FA Section */}
              <section className="mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                  <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                    <i className="fas fa-mobile-screen-button text-primary-500 mr-2"></i>
                    {t('settings.twoFactorAuth')}
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-6">
                    <div className={`p-6 ${settings.two_factor_auth.authenticator_enabled ? 'bg-primary-500/5 border-2 border-primary-500/20' : 'bg-light-100 border-2 border-light-200/20'} rounded-xl`}>
                      <div className="flex items-center mb-3">
                        <div className={`w-10 h-10 ${settings.two_factor_auth.authenticator_enabled ? 'bg-gradient-to-r from-primary-dark to-primary-500' : 'bg-light-200/20'} rounded-lg flex items-center justify-center mr-3`}>
                          <i className={`fas fa-shield-halved text-lg ${settings.two_factor_auth.authenticator_enabled ? 'text-white' : 'text-light-200'}`}></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-dark-300">{t('settings.authenticator')}</h4>
                          <p className="text-xs text-dark-100">{t('settings.mostSecure')}</p>
                        </div>
                      </div>
                      <p className="text-sm text-dark-100 mb-4">{t('settings.authenticatorDesc')}</p>
                      <button 
                        onClick={() => handleToggleChange('two_factor_auth', 'authenticator_enabled')}
                        className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                          settings.two_factor_auth.authenticator_enabled 
                            ? 'bg-red-100 border-2 border-red-200 text-red-600 hover:bg-red-200' 
                            : 'bg-white border-2 border-light-200/30 hover:border-primary-500 text-dark-300'
                        }`}
                      >
                        {settings.two_factor_auth.authenticator_enabled ? t('settings.disable') : t('settings.enable')}
                      </button>
                    </div>
                    
                    <div className={`p-6 ${settings.two_factor_auth.email_2fa_enabled ? 'bg-primary-500/5 border-2 border-primary-500/20' : 'bg-light-100 border-2 border-light-200/20'} rounded-xl`}>
                      <div className="flex items-center mb-3">
                        <div className={`w-10 h-10 ${settings.two_factor_auth.email_2fa_enabled ? 'bg-gradient-to-r from-primary-dark to-primary-500' : 'bg-light-200/20'} rounded-lg flex items-center justify-center mr-3`}>
                          <i className={`fas fa-envelope text-lg ${settings.two_factor_auth.email_2fa_enabled ? 'text-white' : 'text-light-200'}`}></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-dark-300">{t('settings.email2fa')}</h4>
                          <p className="text-xs text-dark-100">{t('settings.backupMethod')}</p>
                        </div>
                      </div>
                      <p className="text-sm text-dark-100 mb-4">{t('settings.email2faDesc')}</p>
                      <button 
                        onClick={() => handleToggleChange('two_factor_auth', 'email_2fa_enabled')}
                        className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                          settings.two_factor_auth.email_2fa_enabled 
                            ? 'bg-red-100 border-2 border-red-200 text-red-600 hover:bg-red-200' 
                            : 'bg-white border-2 border-light-200/30 hover:border-primary-500 text-dark-300'
                        }`}
                      >
                        {settings.two_factor_auth.email_2fa_enabled ? t('settings.disable') : t('settings.enable')}
                      </button>
                    </div>
                    
                    <div className={`p-6 ${settings.two_factor_auth.sms_2fa_enabled ? 'bg-primary-500/5 border-2 border-primary-500/20' : 'bg-light-100 border-2 border-light-200/20'} rounded-xl`}>
                      <div className="flex items-center mb-3">
                        <div className={`w-10 h-10 ${settings.two_factor_auth.sms_2fa_enabled ? 'bg-gradient-to-r from-primary-dark to-primary-500' : 'bg-light-200/20'} rounded-lg flex items-center justify-center mr-3`}>
                          <i className={`fas fa-comment-sms text-lg ${settings.two_factor_auth.sms_2fa_enabled ? 'text-white' : 'text-light-200'}`}></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-dark-300">{t('settings.sms2fa')}</h4>
                          <p className="text-xs text-dark-100">{t('settings.alternative')}</p>
                        </div>
                      </div>
                      <p className="text-sm text-dark-100 mb-4">{t('settings.sms2faDesc')}</p>
                      <button 
                        onClick={() => handleToggleChange('two_factor_auth', 'sms_2fa_enabled')}
                        className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                          settings.two_factor_auth.sms_2fa_enabled 
                            ? 'bg-red-100 border-2 border-red-200 text-red-600 hover:bg-red-200' 
                            : 'bg-white border-2 border-light-200/30 hover:border-primary-500 text-dark-300'
                        }`}
                      >
                        {settings.two_factor_auth.sms_2fa_enabled ? t('settings.disable') : t('settings.enable')}
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              {/* Active Sessions */}
              <section className="mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-dark-300 flex items-center">
                      <i className="fas fa-clock-rotate-left text-primary-500 mr-2"></i>
                      {t('settings.activeSessions')}
                    </h3>
                    <button 
                      onClick={handleSignOutAllDevices}
                      className="text-sm text-red-600 hover:text-red-700 font-semibold flex items-center"
                    >
                      <i className="fas fa-right-from-bracket mr-2"></i>
                      {t('settings.signOutAllDevices')}
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {settings.active_sessions.length === 0 ? (
                      <p className="text-center text-dark-100 py-8">{t('settings.noSessions')}</p>
                    ) : (
                      settings.active_sessions.map((session, index) => (
                        <div 
                          key={session.session_id} 
                          className={`p-5 ${session.is_current ? 'bg-primary-500/5 border-2 border-primary-500/20' : 'bg-light-100 border-2 border-light-200/20'} rounded-xl`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-4">
                              <div className={`w-12 h-12 ${session.is_current ? 'bg-gradient-to-r from-primary-dark to-primary-500' : 'bg-primary-dark'} rounded-lg flex items-center justify-center`}>
                                <i className={`fas ${session.device_type === 'mobile' ? 'fa-mobile-screen' : session.device_type === 'tablet' ? 'fa-tablet-screen-button' : 'fa-desktop'} text-white text-xl`}></i>
                              </div>
                              <div>
                                <h4 className="font-bold text-dark-300 mb-1 flex items-center">
                                  {session.device_name}
                                  {session.is_current && <span className="ml-2 px-2 py-0.5 bg-primary-500 text-white text-xs font-bold rounded-full">{t('settings.current')}</span>}
                                </h4>
                                <p className="text-sm text-dark-100 mb-2">{session.location} • {session.ip_address}</p>
                                <div className="flex items-center space-x-4 text-xs text-dark-100">
                                  <span><i className="fas fa-clock mr-1"></i>{formatTimeAgo(session.last_active)}</span>
                                  <span><i className="fas fa-calendar mr-1"></i>{t('settings.signedIn')}: {formatDate(session.signed_in)}</span>
                                </div>
                              </div>
                            </div>
                            {!session.is_current && (
                              <button 
                                onClick={() => handleSignOutSession(session.session_id)}
                                className="px-4 py-2 bg-white border-2 border-light-200/30 hover:border-red-500 hover:text-red-600 rounded-lg font-semibold text-dark-300 transition-all text-sm"
                              >
                                {t('settings.signOut')}
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </section>
            </>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <section className="mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                  <i className="fas fa-bell text-primary-500 mr-2"></i>
                  {t('settings.loginAlerts')}
                </h3>
                
                <div className="space-y-5">
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">{t('settings.emailAlertsNewLogin')}</div>
                      <div className="text-xs text-dark-100 mt-1">{t('settings.emailAlertsNewLoginDesc')}</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        checked={settings.login_alerts.email_alerts_new_login}
                        onChange={() => handleToggleChange('login_alerts', 'email_alerts_new_login')}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.login_alerts.email_alerts_new_login ? 'translate-x-6' : ''}`}></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">{t('settings.failedLoginAttempts')}</div>
                      <div className="text-xs text-dark-100 mt-1">{t('settings.failedLoginAttemptsDesc')}</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        checked={settings.login_alerts.failed_login_attempts}
                        onChange={() => handleToggleChange('login_alerts', 'failed_login_attempts')}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.login_alerts.failed_login_attempts ? 'translate-x-6' : ''}`}></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">{t('settings.unusualActivity')}</div>
                      <div className="text-xs text-dark-100 mt-1">{t('settings.unusualActivityDesc')}</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        checked={settings.login_alerts.unusual_activity}
                        onChange={() => handleToggleChange('login_alerts', 'unusual_activity')}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.login_alerts.unusual_activity ? 'translate-x-6' : ''}`}></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">{t('settings.passwordChangeConfirmation')}</div>
                      <div className="text-xs text-dark-100 mt-1">{t('settings.passwordChangeConfirmationDesc')}</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        checked={settings.login_alerts.password_change_confirmation}
                        onChange={() => handleToggleChange('login_alerts', 'password_change_confirmation')}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.login_alerts.password_change_confirmation ? 'translate-x-6' : ''}`}></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">{t('settings.securityRecommendations')}</div>
                      <div className="text-xs text-dark-100 mt-1">{t('settings.securityRecommendationsDesc')}</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        checked={settings.login_alerts.security_recommendations}
                        onChange={() => handleToggleChange('login_alerts', 'security_recommendations')}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.login_alerts.security_recommendations ? 'translate-x-6' : ''}`}></div>
                    </label>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <section className="mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                  <i className="fas fa-user-shield text-primary-500 mr-2"></i>
                  {t('settings.dataPrivacy')}
                </h3>
                
                <div className="space-y-6">
                  <div className="p-6 bg-light-100 border-2 border-light-200/20 rounded-xl">
                    <h4 className="font-bold text-dark-300 mb-4">{t('settings.dataRetention')}</h4>
                    <p className="text-sm text-dark-100 mb-4">{t('settings.dataRetentionDesc')}</p>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <button 
                        onClick={() => setSettings(prev => ({...prev, data_privacy: {...prev.data_privacy, data_retention_days: 30}}))}
                        className={`p-4 rounded-lg text-center transition-all ${
                          settings.data_privacy.data_retention_days === 30 
                            ? 'bg-white border-2 border-primary-500' 
                            : 'bg-white border-2 border-light-200/30 hover:border-primary-500'
                        }`}
                      >
                        <div className={`text-2xl font-bold mb-1 ${settings.data_privacy.data_retention_days === 30 ? 'text-primary-500' : 'text-dark-300'}`}>{t('settings.days30')}</div>
                        <div className="text-xs text-dark-100">{t('settings.minimalStorage')}</div>
                      </button>
                      
                      <button 
                        onClick={() => setSettings(prev => ({...prev, data_privacy: {...prev.data_privacy, data_retention_days: 90}}))}
                        className={`p-4 rounded-lg text-center transition-all ${
                          settings.data_privacy.data_retention_days === 90 
                            ? 'bg-white border-2 border-primary-500' 
                            : 'bg-white border-2 border-light-200/30 hover:border-primary-500'
                        }`}
                      >
                        <div className={`text-2xl font-bold mb-1 ${settings.data_privacy.data_retention_days === 90 ? 'text-primary-500' : 'text-dark-300'}`}>{t('settings.days90')}</div>
                        <div className="text-xs text-dark-100">{t('settings.recommended')}</div>
                      </button>
                      
                      <button 
                        onClick={() => setSettings(prev => ({...prev, data_privacy: {...prev.data_privacy, data_retention_days: 365}}))}
                        className={`p-4 rounded-lg text-center transition-all ${
                          settings.data_privacy.data_retention_days === 365 
                            ? 'bg-white border-2 border-primary-500' 
                            : 'bg-white border-2 border-light-200/30 hover:border-primary-500'
                        }`}
                      >
                        <div className={`text-2xl font-bold mb-1 ${settings.data_privacy.data_retention_days === 365 ? 'text-primary-500' : 'text-dark-300'}`}>{t('settings.year1')}</div>
                        <div className="text-xs text-dark-100">{t('settings.maximum')}</div>
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="p-5 bg-light-100 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-dark-300">{t('settings.exportData')}</h4>
                        <i className="fas fa-download text-primary-500 text-xl"></i>
                      </div>
                      <p className="text-sm text-dark-100 mb-4">{t('settings.exportDataDesc')}</p>
                      <button className="w-full px-4 py-2 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 rounded-lg font-semibold text-dark-300 transition-all text-sm">
                        {t('settings.requestDataExport')}
                      </button>
                    </div>
                    
                    <div className="p-5 bg-light-100 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-dark-300">{t('settings.deleteAccount')}</h4>
                        <i className="fas fa-trash text-red-600 text-xl"></i>
                      </div>
                      <p className="text-sm text-dark-100 mb-4">{t('settings.deleteAccountDesc')}</p>
                      <button className="w-full px-4 py-2 bg-white border-2 border-red-200 hover:border-red-500 hover:bg-red-50 text-red-600 rounded-lg font-semibold transition-all text-sm">
                        {t('settings.deleteMyAccount')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <section className="mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                  <i className="fas fa-lock text-primary-500 mr-2"></i>
                  {t('settings.advancedSecurity')}
                </h3>
                
                <div className="space-y-5">
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">{t('settings.require2fa')}</div>
                      <div className="text-xs text-dark-100 mt-1">{t('settings.require2faDesc')}</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        checked={settings.advanced_security.require_2fa_for_sensitive_actions}
                        onChange={() => handleToggleChange('advanced_security', 'require_2fa_for_sensitive_actions')}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.advanced_security.require_2fa_for_sensitive_actions ? 'translate-x-6' : ''}`}></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">{t('settings.autoLogout')}</div>
                      <div className="text-xs text-dark-100 mt-1">{t('settings.autoLogoutDesc')}</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        checked={settings.advanced_security.auto_logout_inactive_minutes > 0}
                        onChange={() => setSettings(prev => ({
                          ...prev, 
                          advanced_security: {
                            ...prev.advanced_security, 
                            auto_logout_inactive_minutes: prev.advanced_security.auto_logout_inactive_minutes > 0 ? 0 : 30
                          }
                        }))}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.advanced_security.auto_logout_inactive_minutes > 0 ? 'translate-x-6' : ''}`}></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">{t('settings.ipAllowlist')}</div>
                      <div className="text-xs text-dark-100 mt-1">{t('settings.ipAllowlistDesc')}</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        checked={settings.advanced_security.ip_allowlist_enabled}
                        onChange={() => handleToggleChange('advanced_security', 'ip_allowlist_enabled')}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.advanced_security.ip_allowlist_enabled ? 'translate-x-6' : ''}`}></div>
                    </label>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* API Keys Tab – Crawler (OpenAI, Anthropic, SERPAPI, Inference) */}
          {activeTab === 'apikeys' && (
            <section className="mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                <h3 className="text-lg font-bold text-dark-300 mb-2 flex items-center">
                  <i className="fas fa-key text-primary-500 mr-2"></i>
                  {t('settings.apiKeys') || 'Crawler API Keys'}
                </h3>
                <p className="text-sm text-dark-100 mb-6">
                  {t('settings.apiKeysDesc') || 'Store your API keys for extraction and search. They are used when you run crawls or auto-search (logged-in user). Leave blank to use server defaults.'}
                </p>
                <div className="space-y-4 max-w-2xl">
                  <div>
                    <label className="block text-sm font-semibold text-dark-300 mb-1">OpenAI API Key</label>
                    <input type="password" placeholder={apiKeysMasked?.openai_api_key_set ? (apiKeysMasked.openai_api_key_preview || '••••••••') : 'sk-...'} className="w-full px-4 py-2 border border-light-200/30 rounded-lg bg-white text-dark-300" value={apiKeysForm.openai_api_key} onChange={(e) => setApiKeysForm(prev => ({ ...prev, openai_api_key: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-300 mb-1">Anthropic API Key</label>
                    <input type="password" placeholder={apiKeysMasked?.anthropic_api_key_set ? (apiKeysMasked.anthropic_api_key_preview || '••••••••') : 'sk-ant-...'} className="w-full px-4 py-2 border border-light-200/30 rounded-lg bg-white text-dark-300" value={apiKeysForm.anthropic_api_key} onChange={(e) => setApiKeysForm(prev => ({ ...prev, anthropic_api_key: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-300 mb-1">SERPAPI Key (Search)</label>
                    <input type="password" placeholder={apiKeysMasked?.serpapi_api_key_set ? (apiKeysMasked.serpapi_api_key_preview || '••••••••') : '...'} className="w-full px-4 py-2 border border-light-200/30 rounded-lg bg-white text-dark-300" value={apiKeysForm.serpapi_api_key} onChange={(e) => setApiKeysForm(prev => ({ ...prev, serpapi_api_key: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-300 mb-1">Inference API Key (Crawler)</label>
                    <input type="password" placeholder={apiKeysMasked?.inference_api_key_set ? (apiKeysMasked.inference_api_key_preview || '••••••••') : 'inference-...'} className="w-full px-4 py-2 border border-light-200/30 rounded-lg bg-white text-dark-300" value={apiKeysForm.inference_api_key} onChange={(e) => setApiKeysForm(prev => ({ ...prev, inference_api_key: e.target.value }))} />
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-4">
                  <button type="button" onClick={loadApiKeys} className="px-4 py-2 bg-light-100 hover:bg-light-200/20 rounded-lg text-sm font-medium text-dark-300">
                    {t('schemaEditor.resetToDefault')}
                  </button>
                  <button type="button" onClick={handleSaveApiKeys} disabled={apiKeysSaving} className="px-4 py-2 bg-gradient-to-r from-primary-dark to-primary-500 hover:opacity-90 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                    {apiKeysSaving ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* General Tab - Default/Placeholder */}
          {activeTab === 'general' && (
            <section className="mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8 text-center py-20">
                <i className="fas fa-sliders text-6xl text-light-200 mb-4"></i>
                <h3 className="text-xl font-bold text-dark-300 mb-2">{t('settings.generalSettings')}</h3>
                <p className="text-dark-100">{t('settings.generalPlaceholder')}</p>
                <p className="text-sm text-dark-100 mt-4">{t('settings.exploreOther')}</p>
              </div>
            </section>
          )}
          
        </main>

        {/* Right Sidebar */}
        <aside className="w-96 bg-white border-l border-light-200/20 overflow-y-auto">
          {/* Security Score Section */}
          <section className="p-6 border-b border-light-200/20">
            <h3 className="text-sm font-bold text-dark-300 mb-4 uppercase tracking-wide">{t('settings.securityScore')}</h3>
            
            <div className={`p-6 bg-gradient-to-br ${getSecurityScoreColor(settings.security_score)} rounded-xl shadow-lg text-white text-center mb-4`}>
              <div className="text-6xl font-bold mb-2">{settings.security_score}</div>
              <div className="text-sm uppercase tracking-wide opacity-90">{getSecurityScoreText(settings.security_score)}</div>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-xs opacity-90">
                  {settings.security_score >= 80 ? t('settings.wellProtected') : t('settings.completeSteps')}
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              {settings.two_factor_auth.authenticator_enabled && (
                <div className="p-3 bg-primary-500/5 border-l-4 border-primary-500 rounded-r-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-dark-300">{t('settings.twoFaEnabled')}</span>
                    <i className="fas fa-check-circle text-primary-500"></i>
                  </div>
                  <p className="text-xs text-dark-100">{t('settings.points30')}</p>
                </div>
              )}
              
              {settings.last_password_change && (
                <div className="p-3 bg-primary-500/5 border-l-4 border-primary-500 rounded-r-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-dark-300">{t('settings.passwordUpdated')}</span>
                    <i className="fas fa-check-circle text-primary-500"></i>
                  </div>
                  <p className="text-xs text-dark-100">{t('settings.points25')}</p>
                </div>
              )}
              
              {settings.login_alerts.email_alerts_new_login && (
                <div className="p-3 bg-primary-500/5 border-l-4 border-primary-500 rounded-r-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-dark-300">{t('settings.loginAlertsActive')}</span>
                    <i className="fas fa-check-circle text-primary-500"></i>
                  </div>
                  <p className="text-xs text-dark-100">{t('settings.points20')}</p>
                </div>
              )}
              
              {!settings.two_factor_auth.recovery_codes_generated && (
                <div className="p-3 bg-light-100 border-l-4 border-light-200 rounded-r-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-dark-300">{t('settings.recoveryCodes')}</span>
                    <i className="fas fa-exclamation-circle text-light-200"></i>
                  </div>
                  <p className="text-xs text-dark-100">{t('settings.points13Available')}</p>
                </div>
              )}
            </div>
          </section>

          {/* Security Tips */}
          <section className="p-6 border-b border-light-200/20">
            <h3 className="text-sm font-bold text-dark-300 mb-4 uppercase tracking-wide">{t('settings.securityTips')}</h3>
            
            <div className="space-y-3">
              <div className="p-3 bg-light-100 border-l-4 border-primary-500 rounded-r-lg">
                <p className="text-xs text-dark-100"><strong>{t('settings.tip')}:</strong> {t('settings.tip1')}</p>
              </div>
              
              <div className="p-3 bg-light-100 border-l-4 border-primary-dark rounded-r-lg">
                <p className="text-xs text-dark-100"><strong>{t('settings.tip')}:</strong> {t('settings.tip2')}</p>
              </div>
              
              <div className="p-3 bg-light-100 border-l-4 border-primary-500 rounded-r-lg">
                <p className="text-xs text-dark-100"><strong>{t('settings.tip')}:</strong> {t('settings.tip3')}</p>
              </div>
            </div>
          </section>

          {/* Quick Links */}
          <section className="p-6">
            <h3 className="text-sm font-bold text-dark-300 mb-4 uppercase tracking-wide">{t('settings.quickLinks')}</h3>
            
            <div className="space-y-3">
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/10 rounded-lg flex items-center transition-colors">
                <i className="fas fa-book text-primary-500 mr-3"></i>
                <span className="text-sm font-medium text-dark-300">{t('settings.securityGuide')}</span>
              </button>
              
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/10 rounded-lg flex items-center transition-colors">
                <i className="fas fa-shield text-primary-dark mr-3"></i>
                <span className="text-sm font-medium text-dark-300">{t('settings.privacyPolicy')}</span>
              </button>
              
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/10 rounded-lg flex items-center transition-colors">
                <i className="fas fa-comments text-primary-500 mr-3"></i>
                <span className="text-sm font-medium text-dark-300">{t('settings.contactSupport')}</span>
              </button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

